import React, { useEffect, useRef, useState } from "react";

type Platform =
  | "x"
  | "twitter"
  | "instagram"
  | "tiktok"
  | "youtube"
  | "facebook";

interface SocialPlatformEmbedProps {
  url: string;
  platform: Platform;
  width?: string | number;
  height?: string | number;
  // optional className or style could be added
}

const loadScript = (id: string, src: string) => {
  if (document.getElementById(id)) return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.id = id;
    s.async = true;
    s.src = src;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
};

const isFullTweetUrl = (u: string) => /twitter\.com\/.*\/status\/\d+/.test(u);

/**
 * Component: SocialPlatformEmbed
 *
 * Key points:
 * - We dynamically import 'react-social-media-embed' on the client only (so SSR won't break).
 * - If the dynamic import doesn't provide the needed embed component, we fall back to
 *   manual script approach for that platform.
 */
const SocialPlatformEmbed: React.FC<SocialPlatformEmbedProps> = ({
  url,
  platform,
  width = "100%",
  height = 500,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [embedModule, setEmbedModule] = useState<any | null>(null);
  const [mounted, setMounted] = useState(false);

  // load react-social-media-embed only on client
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    let cancelled = false;

    const render = async () => {
      setStatus("loading");
      // attempt to dynamically import the package
      try {
        const mod = await import("react-social-media-embed").catch(() => null);
        if (cancelled) return;
        const modAny = mod as any;
        setEmbedModule(modAny);

        // If the package provides the embed component we want, we'll let it render in JSX below.
        // If not available, we'll attempt a manual embed below (scripts + DOM insertion).
        // For certain platforms we need to execute the platform script to process injected markup (Instagram / TikTok).
        switch (platform) {
          case "twitter":
          case "x": {
            // If the imported package provides a Twitter/X component, rely on it (rendered in JSX).
            // Otherwise fallback to manual approach using platform widgets.js and createTweet (client-only).
            if (
              !modAny ||
              !(
                modAny.TwitterEmbed ||
                modAny.XEmbed ||
                modAny.X ||
                modAny.Twitter
              )
            ) {
              // manual fallback
              if (!isFullTweetUrl(url)) {
                console.warn(
                  "Twitter embed expects a full tweet URL (https://twitter.com/.../status/ID)"
                );
              }
              // load twitter widgets
              await loadScript(
                "twitter-wjs",
                "https://platform.twitter.com/widgets.js"
              );
              const tw = window.twttr;
              const tweetId = url.split("/").filter(Boolean).pop();
              if (!tweetId) throw new Error("Cannot find tweet id in url");
              if (containerRef.current) {
                containerRef.current.innerHTML = "";
                if (tw?.widgets?.createTweet) {
                  await tw.widgets.createTweet(tweetId, containerRef.current, {
                    conversation: "none",
                    align: "center",
                  });
                } else {
                  // fallback: embed a blockquote with link (Twitter's widget should rewrite it once script loads)
                  containerRef.current.innerHTML = `<blockquote class="twitter-tweet"><a href="${url}"></a></blockquote>`;
                  // twitter script should process it automatically when loaded
                }
              }
            }
            break;
          }

          case "instagram": {
            // prefer package embed; if not available, manual fallback
            if (!modAny || !(modAny.InstagramEmbed || modAny.Instagram)) {
              // instagram embed script must be loaded and then processed
              await loadScript(
                "instagram-embed",
                "https://www.instagram.com/embed.js"
              );
              // The instagram script will look for .instagram-media or like classes; easiest is to insert blockquote
              if (containerRef.current) {
                containerRef.current.innerHTML = `<blockquote class="instagram-media" data-instgrm-permalink="${url}" data-instgrm-version="14"><a href="${url}"></a></blockquote>`;
                // When script loads, call process if available
                if (window.instgrm?.Embeds?.process) {
                  window.instgrm.Embeds.process();
                }
              }
            }
            break;
          }

          case "tiktok": {
            if (!modAny || !(modAny.TikTokEmbed || modAny.TikTok)) {
              // tiktok requires a blockquote with data-video-id or an <iframe>
              // load tiktok embed script
              await loadScript(
                "tiktok-embed",
                "https://www.tiktok.com/embed.js"
              );
              if (containerRef.current) {
                const videoId = url.split("/").filter(Boolean).pop() || "";
                containerRef.current.innerHTML = `
                  <blockquote class="tiktok-embed" cite="${url}" data-video-id="${videoId}" style="max-width: 325px;min-width:288px;">
                    <section></section>
                  </blockquote>
                `;
                // script should autoprocess, but call tiktok global if any
                try {
                  // TikTok embed script processes automatically
                } catch (e) {
                  // ignore
                }
              }
            }
            break;
          }

          case "facebook": {
            // If package did not provide FacebookEmbed, we can insert FB SDK + div.fb-post markup
            if (!modAny || !(modAny.FacebookEmbed || modAny.Facebook)) {
              // FB requires an SDK init; implementing it here is more involved (appId). For quick fallback:
              if (containerRef.current) {
                // simple fallback: iframe plugin (public posts only)
                const encoded = encodeURIComponent(url);
                containerRef.current.innerHTML = `<iframe src="https://www.facebook.com/plugins/post.php?href=${encoded}&show_text=true&width=500" width="${width}" height="${height}" style="border:none;overflow:hidden" scrolling="no" frameBorder="0" allowFullScreen></iframe>`;
              }
            }
            break;
          }

          // youtube handled via iframe below
        }

        setStatus("success");
      } catch (err) {
        console.error("Embed render error:", err);
        setStatus("error");
      }
    };

    render();

    const node = containerRef.current;
    return () => {
      cancelled = true;
      // cleanup DOM insertion (capture ref to avoid stale-ref lint warnings)
      if (node) node.innerHTML = "";
    };
  }, [platform, url, mounted, width, height]);

  // Loading state
  if (status === "loading") {
    return (
      <div className="flex items-center justify-center h-40 bg-gray-50 rounded">
        <span className="animate-spin h-6 w-6 border-2 border-gray-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex items-center justify-center h-40 bg-red-50 rounded">
        <p className="text-red-500 text-sm">Failed to load {platform} embed</p>
      </div>
    );
  }

  // YouTube: simple embed (works server-side too because it's just an iframe URL)
  if (platform === "youtube") {
    // support watch?v= and short links
    let videoId: string | null = null;
    try {
      const possible = new URL(url);
      videoId = possible.searchParams.get("v");
    } catch {
      // not a URL object
    }
    if (!videoId) {
      const parts = url.split("/").filter(Boolean);
      videoId = parts.pop() || null;
    }

    return (
      <iframe
        width={width}
        height={height}
        src={`https://www.youtube.com/embed/${videoId}`}
        title={`YouTube video ${videoId}`}
        allowFullScreen
        loading="lazy"
      />
    );
  }

  // If dynamic module provided the component, use it. Map platform to likely export names.
  if (embedModule) {
    // there are small variations in naming across versions: check common exports
    const Candidate = (() => {
      switch (platform) {
        case "twitter":
        case "x":
          return (
            embedModule.TwitterEmbed ??
            embedModule.XEmbed ??
            embedModule.X ??
            embedModule.Twitter
          );
        case "instagram":
          return embedModule.InstagramEmbed ?? embedModule.Instagram;
        case "tiktok":
          return (
            embedModule.TikTokEmbed ??
            embedModule.TikTok ??
            embedModule.TikTokEmbedComponent
          );
        case "facebook":
          return embedModule.FacebookEmbed ?? embedModule.Facebook;
        default:
          return null;
      }
    })();

    if (Candidate) {
      // Render the component provided by the package.
      // Use key to re-render when URL changes.
      return (
        <div style={{ width, height }} ref={containerRef}>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore - Candidate is a React component from a dynamic import */}
          <Candidate
            url={url}
            width={typeof width === "number" ? width : undefined}
          />
        </div>
      );
    }
  }

  // If we reach here, it's because either:
  // - embedModule wasn't available OR
  // - we already inserted manual markup into containerRef in the effect -> so just render container
  return <div ref={containerRef} style={{ width, height }} />;
};

export default SocialPlatformEmbed;
